"use client";

import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { useAppStore, pushView, initRouteFromUrl, openCourse } from "@/stores/useAppStore";
import { Landing } from "@/components/landing/Landing";
import AuthView from "@/components/auth/AuthView";
import UserDashboard from "@/components/dashboard/UserDashboard";
import AdminPanel from "@/components/admin/AdminPanel";
import { Loader2 } from "lucide-react";

export function AppShell() {
  const { data: session, status } = useSession();
  const view = useAppStore((s) => s.view);
  const selectedCourseSlug = useAppStore((s) => s.selectedCourseSlug);
  const didInit = useRef(false);

  // On first mount: read ?view= and ?ref= and ?course= into the store.
  // On subsequent view changes, sync the URL.
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      initRouteFromUrl();
      // also pick up ?course= slug for the player
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const c = params.get("course");
        if (c) {
          useAppStore.getState().openCourse(c);
        }
      }
      return;
    }
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (view === "landing") {
      url.searchParams.delete("view");
    } else {
      url.searchParams.set("view", view);
    }
    if (view !== "course") url.searchParams.delete("course");
    window.history.replaceState({}, "", url.toString());
  }, [view]);

  const user = session?.user as
    | {
        id: string;
        name?: string;
        phone?: string;
        role?: string;
        vpnAccess?: boolean;
      }
    | undefined;

  const isLoggedIn = status === "authenticated" && !!user;
  const isAdmin = isLoggedIn && user?.role === "ADMIN";
  const hasVpn = isLoggedIn && !!user?.vpnAccess;

  const effectiveView = resolveView(view, isLoggedIn, isAdmin, hasVpn);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ---- Auth views ----
  if (effectiveView === "login" || effectiveView === "register") {
    return <AuthView />;
  }

  // ---- Admin ----
  if (effectiveView === "admin") {
    return <AdminPanel />;
  }

  // ---- Public (logged-out can view academy/course/commission/ai/order-site/landing) ----
  if (effectiveView === "academy") {
    return <AcademyLazy />;
  }
  if (effectiveView === "course") {
    return <CoursePlayerLazy slug={selectedCourseSlug} />;
  }
  if (effectiveView === "commission") {
    return <CommissionLazy />;
  }
  if (effectiveView === "ai-accounts") {
    return <AIAccountsLazy />;
  }
  if (effectiveView === "order-site") {
    return <OrderSiteLazy />;
  }

  // ---- Authenticated user home / VPN ----
  if (effectiveView === "vpn") {
    return <UserDashboard />;
  }
  if (effectiveView === "dashboard" || effectiveView === "payments") {
    return <UserHomeLazy />;
  }

  // Landing (default)
  return <Landing onBuy={() => pushView("register")} />;
}

function resolveView(
  view: string,
  isLoggedIn: boolean,
  isAdmin: boolean,
  hasVpn: boolean,
): string {
  // Admin-only
  if (view === "admin") {
    return isAdmin ? "admin" : "login";
  }
  // VPN management: requires vpnAccess
  if (view === "vpn" || view === "payments") {
    if (!isLoggedIn) return "login";
    if (!hasVpn) return "dashboard";
    return view === "payments" ? "vpn" : "vpn";
  }
  // User dashboard (home) — must be logged in
  if (view === "dashboard") {
    return isLoggedIn ? "dashboard" : "login";
  }
  // Auth pages: logged-in users go to their home
  if (view === "login" || view === "register") {
    return isLoggedIn ? "dashboard" : view;
  }
  // Public-browsable views (academy, course, commission, ai-accounts, order-site, landing)
  return view;
}

// --- Lazy wrappers will be replaced with real components below ---
import dynamic from "next/dynamic";
const AcademyLazy = dynamic(() => import("@/components/academy/Academy").then(m => m.default), { ssr: false });
const CoursePlayerLazy = dynamic<{ slug: string | null }>(() => import("@/components/academy/CoursePlayer").then(m => m.default), { ssr: false });
const CommissionLazy = dynamic(() => import("@/components/commission/CommissionView").then(m => m.default), { ssr: false });
const AIAccountsLazy = dynamic(() => import("@/components/ai/aiAccountsView").then(m => m.default), { ssr: false });
const OrderSiteLazy = dynamic(() => import("@/components/order/OrderSiteView").then(m => m.default), { ssr: false });
const UserHomeLazy = dynamic(() => import("@/components/dashboard/UserHome").then(m => m.default), { ssr: false });
